import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ConfigProvider } from "antd";
import styled from "styled-components";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { useFilters } from "core/features/filters/store";
import { gray300, white } from "core/resources/theme/colors";
import useRelatorioContext from "../../../hooks/useRelatorioContext";
import { FULLSCREEN_LAYER_Z_INDEX } from "../utils";
import { FiltrosAvancado } from "../../../interfaces/Planejamento";
import { useGetMediasClimaticas } from "../hooks/useGetMediasClimaticas";
import { useGetRdoOperacoes } from "../hooks/useGetRdoOperacoes";
import { mapMediasClimaticasToWx } from "./diario/mapMediasClimaticas";
import { mapRdoOperacoesToSections } from "./diario/mapRdoOperacoes";
import WxCard from "./diario/WxCard";
import WxDays from "./diario/WxDays";
import WxSummary from "./diario/WxSummary";
import { WEATHER_ICONS } from "./diario/weatherIcons";
import RdoPeriodoSection from "./diario/RdoPeriodoSection";
import RdoPeriodoSummaryRow from "./diario/RdoPeriodoSummaryRow";
import { Loader } from "./Loader";

type Props = {
  filtrosAvancados?: FiltrosAvancado;
  permission: boolean;
};

function DiarioOperacoes({ permission }: Props) {
  const expandScreen = useRelatorioContext((s) => s.expandScreen);
  const setExpandScreen = useRelatorioContext((s) => s.setExpandScreen);
  const layerRef = useRef<HTMLDivElement>(null);

  const unidadeId = useFilters((s) => s.filters.unidade?.id);
  const safraId = useFilters((s) => s.filters.safra?.id);
  const safraPeriodoIds = useFilters(
    (s) => s.filters.filtersAdvanced?.safraPeriodoIds || []
  );
  const rdoDataModo = useFilters(
    (s) => s.filters.filtersAdvanced?.rdoDataModo || "geral"
  );
  const rdoDataInicio = useFilters(
    (s) => s.filters.filtersAdvanced?.rdoDataInicio
  );
  const rdoDataFim = useFilters((s) => s.filters.filtersAdvanced?.rdoDataFim);

  const { data, isLoading, isError } = useGetMediasClimaticas(
    permission ? safraId : undefined,
    permission ? unidadeId : undefined
  );

  const {
    data: rdoData,
    isLoading: rdoLoading,
    isFetching: rdoFetching,
    isError: rdoError,
  } = useGetRdoOperacoes({ enabled: permission });

  const weather = useMemo(
    () => mapMediasClimaticasToWx(isError ? null : data),
    [data, isError]
  );

  const sections = useMemo(
    () => mapRdoOperacoesToSections(rdoError ? null : rdoData),
    [rdoData, rdoError]
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const filtersKey = [
    safraPeriodoIds.join("|"),
    rdoDataModo,
    rdoDataInicio,
    rdoDataFim,
    unidadeId,
    safraId,
  ].join("::");

  // Filtrar / mudança de filtros operacionais: recolher expansões (RN04)
  useEffect(() => {
    setExpandedRowKeys([]);
  }, [filtersKey]);

  if (!permission) {
    return (
      <h2 style={{ textAlign: "center", padding: 15 }}>
        Você não tem permissão para visualizar.
      </h2>
    );
  }

  const content = (
    <ConfigProvider
      getPopupContainer={() =>
        (expandScreen && layerRef.current) || document.body
      }
    >
      <Container ref={layerRef} expanded={expandScreen}>
        <ExpandButton
          disabled={!permission}
          expanded={expandScreen}
          onClick={() => setExpandScreen((prev) => !prev)}
          title={expandScreen ? "Sair da tela cheia" : "Expandir"}
        >
          {expandScreen ? (
            <MdFullscreenExit size={20} />
          ) : (
            <MdFullscreen size={20} />
          )}
        </ExpandButton>

        <WeatherBlock>
          {isLoading && !data ? (
            <LoaderWrap>
              <Loader />
            </LoaderWrap>
          ) : (
            <WeatherRow>
              <WxCard
                title="Ocorrido | últimos 7 dias"
                iconSrc={WEATHER_ICONS.rain}
              >
                <WxDays days={weather.ocorridoDays} />
                <WxSummary data={weather.ocorridoSummary} />
              </WxCard>
              <WxCard
                title="Previsão | próximos 7 dias"
                iconSrc={WEATHER_ICONS["partly-cloudy-day"]}
              >
                <WxDays days={weather.previsaoDays} />
                <WxSummary data={weather.previsaoSummary} />
              </WxCard>
            </WeatherRow>
          )}
        </WeatherBlock>

        {!!safraPeriodoIds.length &&
          !rdoLoading &&
          !rdoFetching &&
          !rdoError &&
          !!sections.length && (
            <SummaryBlock>
              <RdoPeriodoSummaryRow sections={sections} />
            </SummaryBlock>
          )}

        <TablesBlock>
          {!safraPeriodoIds.length ? (
            <EmptyMsg>
              Selecione ao menos um período de produção nos filtros.
            </EmptyMsg>
          ) : rdoLoading || rdoFetching ? (
            <LoaderWrap>
              <Loader />
            </LoaderWrap>
          ) : rdoError ? (
            <EmptyMsg>Não foi possível carregar as operações.</EmptyMsg>
          ) : !sections.length ? (
            <EmptyMsg>Nenhum período com dados para os filtros atuais.</EmptyMsg>
          ) : (
            sections.map((section) => (
              <RdoPeriodoSection
                key={section.safraPeriodoId}
                section={section}
                filtersKey={filtersKey}
                expandedRowKeys={expandedRowKeys}
                onExpandedRowKeysChange={setExpandedRowKeys}
              />
            ))
          )}
        </TablesBlock>
      </Container>
    </ConfigProvider>
  );

  if (expandScreen && typeof document !== "undefined") {
    return createPortal(content, document.body);
  }

  return content;
}

export default DiarioOperacoes;

const Container = styled.div<{ expanded: boolean }>`
  background: #fff;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: ${({ expanded }) => (expanded ? "0" : "8px")};
  padding: ${({ expanded }) => (expanded ? "20px" : "0 0 12px")};
  z-index: ${({ expanded }) => (expanded ? FULLSCREEN_LAYER_Z_INDEX : "auto")};
  height: ${({ expanded }) => (expanded ? "100vh" : "auto")};
  max-height: ${({ expanded }) => (expanded ? "100vh" : "calc(100vh - 120px)")};
  min-height: ${({ expanded }) => (expanded ? "100vh" : "200px")};

  ${({ expanded }) =>
    expanded &&
    `
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
  `}
`;

const ExpandButton = styled.button<{ expanded: boolean }>`
  position: absolute;
  top: ${({ expanded }) => (expanded ? "10px" : "4px")};
  right: ${({ expanded }) => (expanded ? "20px" : "2px")};
  z-index: 20;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WeatherBlock = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
  flex-shrink: 0;
  margin-bottom: 4px;
`;

const SummaryBlock = styled.div`
  position: relative;
  z-index: 0;
  min-width: 0;
  flex-shrink: 0;
  margin-right: 32px;

  @media (max-width: 1100px) {
    margin-right: 36px;
  }
`;

const LoaderWrap = styled.div`
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WeatherRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding-right: 32px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    padding-right: 36px;
  }
`;

const TablesBlock = styled.div`
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  margin-right: 32px;
  border: 1px solid ${gray300};
  border-radius: 8px;
  background: ${white};
  overflow-x: auto;
  overflow-y: visible;
  flex-shrink: 0;

  @media (max-width: 1100px) {
    margin-right: 36px;
  }
`;

const EmptyMsg = styled.p`
  margin: 0;
  padding: 24px 8px;
  text-align: center;
  color: #697b98;
  font-size: 0.9rem;
`;
